<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item ">
                <div class="title">
                    <h3>Unlinked Enterprise Agreements</h3>
                </div>
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <EntAgreementFilter @onChanged="entAgreementFilterChangedHandler" />
                    </div>
                    <div class="custom-toolbar-end">
                        <SearchComponent
                            v-if="$root.isDesktop"
                            :search-filter="searchText"
                            placeholder="Transaction Id, Company, Email"
                            @onSearch="searchComponentSearchHandler"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-blue">
                        <div class="card-icon">
                            <md-icon>payment</md-icon>
                        </div>
                    </md-card-header>
                    <md-card-content class="body-list">
                        <template v-if="!searchResults || !searchResults.length">
                            <p>No result found.</p>
                        </template>
                        <template v-else>
                            <div
                                :class="['bulk-section', topScrollPosition > 150 ? 'sticky' : '']"
                                v-if="selectedEntAgreementIds.length"
                            >
                                <div>
                                    {{ selectedEntAgreementIds.length }} Enterprise Agreement{{
                                        selectedEntAgreementIds.length > 1 ? 's' : ''
                                    }}
                                    selected.
                                </div>
                                <div>
                                    <md-button
                                        class="md-primary btn-map-to-team"
                                        title="Map to a team"
                                        @click.prevent="openMapping"
                                    >
                                        Map to a team
                                    </md-button>
                                </div>
                            </div>
                            <div class="column">
                                <md-table>
                                    <md-table-row>
                                        <md-table-head>
                                            <md-checkbox
                                                class="checkbox-all button-gray"
                                                v-model="isSelectAll"
                                                @change="onSelectAll(isSelectAll)"
                                            ></md-checkbox>
                                        </md-table-head>
                                        <md-table-head>Client Name</md-table-head>
                                        <md-table-head>Transaction Id</md-table-head>
                                        <md-table-head>Status</md-table-head>
                                        <md-table-head>Product</md-table-head>
                                        <md-table-head>Agreement Date</md-table-head>
                                        <md-table-head>Payment Date</md-table-head>
                                        <md-table-head>Amount</md-table-head>
                                        <md-table-head>Payment Source</md-table-head>
                                        <md-table-head>&nbsp;</md-table-head>
                                    </md-table-row>
                                    <md-table-row
                                        class="ea"
                                        v-for="(item, itemIndex) in searchResults"
                                        :key="`licenseDetails-${itemIndex}`"
                                    >
                                        <md-table-cell>
                                            <md-checkbox
                                                class="ea-checkbox"
                                                :value="item.enterpriseAgreementId"
                                                v-model="selectedEntAgreementIds"
                                            ></md-checkbox>
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.clientName }}
                                            <br />
                                            <i>{{ item.clientCompanyName }}</i>
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.transactionId }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.status }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.productName }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.agreementDate | dateFormat(DATE_TYPES.internationalDate) }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.paymentDate | dateFormat(DATE_TYPES.internationalDate) }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ `${item.currency} ${formatNumber(item.amount, 2)}` }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            {{ item.paymentSource }}
                                        </md-table-cell>
                                        <md-table-cell>
                                            <md-button
                                                title="View Details"
                                                class="md-info md-just-icon md-round md-primary"
                                                @click.prevent="showEAItems(item)"
                                            >
                                                <md-icon>payment</md-icon>
                                            </md-button>
                                        </md-table-cell>
                                    </md-table-row>
                                </md-table>
                            </div>
                        </template>
                    </md-card-content>
                    <md-card-actions v-if="total" class="page-footer" md-alignment="space-between">
                        <div>
                            <p v-if="total === pagination.perPage" class="card-category">
                                Page {{ pagination.currentPage }} of many
                            </p>
                            <p v-else class="card-category">Page {{ pagination.currentPage }} of {{ totalPages }}</p>
                        </div>
                        <Pagination
                            v-model="pagination.currentPage"
                            class="pagination-no-border pagination-success"
                            :per-page="pagination.perPage"
                            :total="total"
                            :enable-first-page-link="true"
                            :enable-last-page-link="true"
                            @change-page="handleChangePage($event, pagination.perPage)"
                        />
                    </md-card-actions>
                </md-card>
            </div>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { Pagination, SearchComponent } from '@/components';
import EntAgreementItemsModal from './components/EntAgreementItemsModal';
import MapEntAgreementToTeamModal from './components/MapEntAgreementToTeamModal';
import EntAgreementFilter from './components/EntAgreementFilter';

export default {
    name: 'EAList',
    components: { SearchComponent, Pagination, EntAgreementFilter },
    mixins: [GeneralMixin],
    data() {
        return {
            searchText: '',
            isSelectAll: false,
            searchResults: [],
            selectedEntAgreementIds: [],
            filters: {
                status: 'all',
                paymentSource: 'all',
                productType: 'all'
            },
            pagination: {
                perPage: 15,
                currentPage: 1,
                total: 0
            },
            topScrollPosition: 0
        };
    },
    computed: {
        total() {
            return this.pagination.total;
        },
        totalPages() {
            if (this.total > 0) {
                return Math.ceil(this.total / this.pagination.perPage);
            }
            return 1;
        }
    },
    watch: {
        selectedEntAgreementIds: {
            handler(newVal) {
                const allIds = this.searchResults.map((item) => item.enterpriseAgreementId);

                if (!newVal.length) {
                    this.isSelectAll = false;
                    return;
                }

                // Check if all are selected
                const allSelected = allIds.every((id) => newVal.includes(id));

                // Only update `selectAll` if needed (avoid triggering @change)
                if (this.isSelectAll !== allSelected) {
                    this.isSelectAll = allSelected;
                }
            },
            deep: true
        }
    },
    methods: {
        async getEA() {
            this.$_handleLoaderState(true, 'Fetching Enterprise Agreements');
            try {
                this.selectedEntAgreementIds = [];

                const payload = {
                    method: 'POST',
                    data: {
                        status: this.filters.status === 'all' ? '' : this.filters.status,
                        paymentSource: this.filters.paymentSource === 'all' ? '' : this.filters.paymentSource,
                        productType: this.filters.productType === 'all' ? '' : this.filters.productType,
                        searchText: this.searchText,
                        pageNumber: this.pagination.currentPage,
                        itemsPerPage: this.pagination.perPage
                    }
                };
                const api = `/api/billings/support/unlinked-ent-agreements`;
                const { data } = await handleRequests(api, payload);

                if (data && data.total) {
                    this.pagination.total = data.total;
                    this.searchResults = [...data.data];
                } else {
                    this.searchResults = [];
                    this.pagination.total = 0;
                }
            } catch (e) {
                const message = 'Unable to retrieve Enterprise Agreements.';
                showErrorMessage(this, message, e);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        showEAItems(item) {
            this.$modal.show(EntAgreementItemsModal, {
                transactionId: item.transactionId,
                currency: item.currency,
                items: item.enterpriseAgreementItems || []
            });
        },
        async handleChangePage(currentPage = 1, perPage = 50) {
            this.pagination.currentPage = currentPage;
            this.pagination.perPage = perPage;

            await this.getEA();
        },
        async searchComponentSearchHandler(arg) {
            this.searchText = arg?.searchText ?? '';

            this.pagination.currentPage = 1;
            await this.getEA();
        },
        async entAgreementFilterChangedHandler(args) {
            this.filters = { ...args };
            this.pagination.currentPage = 1;
            await this.getEA();
        },
        formatNumber(num, digit) {
            return (Math.round(num * 100) / 100).toFixed(digit).toLocaleString();
        },
        onSelectAll(value) {
            if (this.isSelectAll) {
                // Select all
                this.selectedEntAgreementIds = this.searchResults.map((x) => x.enterpriseAgreementId);
            } else {
                // Deselect all
                this.selectedEntAgreementIds = [];
            }
        },
        async openMapping(item) {
            await this.$modal
                .show(MapEntAgreementToTeamModal, {
                    selectedEntAgreementIds: this.selectedEntAgreementIds
                })
                .then((result) => {
                    if (result && result === true) {
                        this.searchResults = this.searchResults.filter(
                            (item) => !this.selectedEntAgreementIds.includes(item.enterpriseAgreementId)
                        );

                        this.selectedEntAgreementIds = [];
                    }
                });
        }
    },
    async mounted() {
        await this.getEA();
    }
};
</script>

<style lang="scss" scoped>
.md-checkbox {
    margin: 0 !important;
}
.body-list {
    position: relative;
    padding-top: 30px;
    .sticky {
        position: fixed;
        top: 0;
        z-index: 9;
        left: 75px;
    }
}
.bulk-section {
    position: absolute;
    top: -18px;
    width: 545px;
    margin-left: auto;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
    background-color: #cfeefa;
    color: #2b93ff;
    font-weight: 400;
    padding: 5px 10px 5px 20px;
    > div {
        //width: 60%;
        display: inline-block;
        vertical-align: middle;
        text-align: left;
    }
    > div:last-child {
        width: 40%;
        text-align: right;
        button {
            height: 30px;
            // width: 30px;
            min-width: 30px;
        }
    }
}

.btn-map-to-team {
    width: 120px;
}
</style>
